import type { TaskWithAssignments } from './task/assignment'
import type { Ticket } from './ticket'

export type FocusItemModuleType = 'TASK' | 'TICKET'

/**
 * Benny-curated daily focus plan. Generated once per day per user by a
 * single AI pass, then edited locally (done/skip/add-more) without further
 * AI calls. Persisted server-side so the plan is remembered all day.
 */

export type FocusItemState = 'pending' | 'done' | 'skipped'

export interface FocusPlanItem {
  /** Stable key within the plan (also used for ordering ties) */
  key: string
  /** Absent on plans saved before tickets support — treat as TASK */
  moduleType?: FocusItemModuleType
  /** Task or ticket id, depending on moduleType */
  taskId: string
  /** Theme the item was clustered under, e.g. "PawTend quick wins" */
  group: string
  /** Where the work lives, e.g. "PawTend Development · PawTend" — snapshot at curation time */
  contextLabel?: string
  /** Benny's one-line reason this made the cut */
  reason?: string
  /** Rough effort estimate — display only, never used for sorting */
  estimateMinutes?: number | null
  order: number
  state: FocusItemState
}

export interface FocusPlanSettings {
  /** Include open tasks the user follows but isn't assigned to */
  includeFollowed: boolean
  /** Include unassigned tasks from projects the user is a member of */
  includeUnassigned: boolean
  /** Include open tickets assigned to the user */
  includeTickets: boolean
  /** Hard cap on picks per generation (default 7) */
  maxItems: number
}

export const DEFAULT_FOCUS_PLAN_SETTINGS: FocusPlanSettings = {
  includeFollowed: true,
  includeUnassigned: false,
  includeTickets: false,
  maxItems: 7,
}

/** An URGENT/HIGH assigned open item that today's plan does not include */
export interface FocusLeftOutItem {
  id: string
  moduleType: FocusItemModuleType
  name: string
  priority: string
  dueDate: string | null
}

export interface FocusPlanResponse {
  id: string
  planDate: string
  /** Benny's morning brief */
  summary: string | null
  settings: FocusPlanSettings
  items: FocusPlanItem[]
  /** Summary task rows for every task item, for list rendering + detail-panel seeds */
  tasks: TaskWithAssignments[]
  /** Summary ticket rows for every ticket item */
  tickets: Ticket[]
  /**
   * URGENT/HIGH assigned open work missing from the plan, recomputed on every
   * read so it stays honest as work completes or new urgent work appears.
   * Assigned-only on purpose: followed/unassigned candidates are opt-in
   * extras, and tickets count only when the plan's settings include tickets.
   */
  highPriorityLeftOut: FocusLeftOutItem[]
  generatedAt: string | Date
}

/** One row per saved plan, for the history date picker */
export interface FocusPlanHistoryEntry {
  planDate: string
  /** Non-skipped picks that day */
  itemCount: number
  doneCount: number
}

export interface FocusPlanHistoryResponse {
  entries: FocusPlanHistoryEntry[]
}

export interface GenerateFocusPlanRequest {
  settings?: Partial<FocusPlanSettings>
  /** Optional steer for Benny, e.g. "prioritize PawTend today" */
  instruction?: string
}

export interface FocusMoreWorkRequest {
  /** How many additional items to pull in (default 3) */
  count?: number
  instruction?: string
}

export interface UpdateFocusItemRequest {
  state: FocusItemState
}
